import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Modal,
  Button,
  Table,
  Form,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap'
import moment from 'moment'
import { useToasts } from 'react-toast-notifications'
import {
  BillingForItau,
  getContractBillingsForItau,
  generateItauBoletos,
  resendBolecodeEmail,
  downloadBoletoPDF,
  previewItauValue,
  syncUauPayment,
  cancelItauBolecode
} from '../../../../services/itau-boletos'
import { convertNumberToCurrencyFormat } from '../../../../utils/convertNumberToCurrencyFormat'
import { AppContext } from '../../../../app'

interface Props {
  show: boolean
  contractId: string
  onClose: () => void
  onGenerated: () => void
}

interface PaymentPartGroup {
  name: string
  billings: BillingForItau[]
}

export const GenerateItauBoletosModal: React.FC<Props> = ({
  show,
  contractId,
  onClose,
  onGenerated
}) => {
  const { addToast } = useToasts()
  const { user } = useContext(AppContext)
  const [billings, setBillings] = useState<BillingForItau[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [filterParcela, setFilterParcela] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  // Per-billing edited date, preview breakdown, waivePenalties flag
  const [rowOverrides, setRowOverrides] = useState<{
    [id: string]: {
      date: string
      previewValue?: number
      previewSemPenalidades?: number
      previewMulta?: number
      previewJuros?: number
      previewing?: boolean
      waivePenalties?: boolean
    }
  }>({})

  const setRowDate = (id: string, date: string) => {
    setRowOverrides((prev) => ({ ...prev, [id]: { ...prev[id], date } }))
  }

  const toggleWaivePenalties = (id: string) => {
    setRowOverrides((prev) => ({
      ...prev,
      [id]: { ...prev[id], waivePenalties: !prev[id]?.waivePenalties }
    }))
  }

  const fetchPreview = async (id: string, date: string | undefined) => {
    setRowOverrides((prev) => ({
      ...prev,
      [id]: { ...prev[id], date: date || prev[id]?.date, previewing: true }
    }))
    try {
      const result = await previewItauValue(
        id,
        date,
        user?.token || '',
        user?.selectedContextEncoded
      )
      setRowOverrides((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          date: date || prev[id]?.date,
          previewValue: result?.valorReajustado,
          previewSemPenalidades: result?.valorSemPenalidades,
          previewMulta: result?.valorMulta,
          previewJuros: result?.valorJurosAtraso,
          previewing: false
        }
      }))
    } catch (err: any) {
      setRowOverrides((prev) => ({
        ...prev,
        [id]: { ...prev[id], date: date || prev[id]?.date, previewing: false }
      }))
      addToast(err.response?.data?.message || 'Erro ao calcular preview', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const clearRowOverride = (id: string) => {
    setRowOverrides((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      addToast(`${label} copiado!`, {
        appearance: 'success',
        autoDismiss: true
      })
    })
  }

  const handleResendEmail = async (billingId: string) => {
    if (
      !window.confirm('Deseja reenviar o e-mail com o bolecode para o cliente?')
    )
      return
    setResendingId(billingId)
    try {
      await resendBolecodeEmail(
        billingId,
        user?.token || '',
        user?.selectedContextEncoded
      )
      addToast('E-mail reenviado com sucesso!', {
        appearance: 'success',
        autoDismiss: true
      })
    } catch (err: any) {
      addToast(err.response?.data?.message || 'Erro ao reenviar e-mail', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setResendingId(null)
    }
  }

  const handleSyncUau = async (billingId: string) => {
    if (
      !window.confirm(
        'Registrar pagamento deste bolecode no UAU? Operação irreversível em produção.'
      )
    )
      return
    setSyncingId(billingId)
    try {
      const result = await syncUauPayment(
        billingId,
        user?.token || '',
        user?.selectedContextEncoded
      )
      let label: string
      let appearance: 'success' | 'warning' = 'success'
      if (result.status === 'registered') {
        const parts: string[] = []
        if (result.valorAcrescimo)
          parts.push(`+acréscimo R$ ${result.valorAcrescimo.toFixed(2)}`)
        if (result.valorDesconto)
          parts.push(`−desconto R$ ${result.valorDesconto.toFixed(2)}`)
        const extras = parts.length > 0 ? ` (${parts.join(', ')})` : ''
        label = `Registrado UAU (NumReceb=${result.numReceb ?? '?'}, R$ ${(
          result.valorRegistered ?? 0
        ).toFixed(2)}${extras})`
        if (result.lossy) {
          label += ' — ATENÇÃO: valor UAU > pago; reconciliar banco manualmente'
          appearance = 'warning'
        }
      } else if (result.status === 'already_registered') {
        label = `Já existe no UAU (NumReceb=${result.numReceb})`
      } else {
        label = 'Já sincronizado anteriormente'
      }
      addToast(label, { appearance, autoDismiss: !result.lossy })
      await fetchBillings()
    } catch (err: any) {
      addToast(err.response?.data?.message || 'Erro ao sincronizar com UAU', {
        appearance: 'error',
        autoDismiss: false
      })
    } finally {
      setSyncingId(null)
    }
  }

  const handleCancelBolecode = async (billingId: string) => {
    if (
      !window.confirm(
        'Cancelar bolecode Itaú desta parcela? O cliente não poderá mais pagar por esse boleto. Use quando parcela foi renegociada no UAU.'
      )
    )
      return
    setCancellingId(billingId)
    try {
      const result = await cancelItauBolecode(
        billingId,
        user?.token || '',
        user?.selectedContextEncoded
      )
      const suffix = result.itauCancelled
        ? 'cancelado no Itaú'
        : `Itaú não cancelou (${
            result.itauCancelError || 'motivo desconhecido'
          }), campos locais limpos`
      addToast(`Bolecode ${result.oldNossoNumero} — ${suffix}`, {
        appearance: result.itauCancelled ? 'success' : 'warning',
        autoDismiss: result.itauCancelled
      })
      await fetchBillings()
    } catch (err: any) {
      addToast(err.response?.data?.message || 'Erro ao cancelar bolecode', {
        appearance: 'error',
        autoDismiss: false
      })
    } finally {
      setCancellingId(null)
    }
  }

  const handleDownloadPDF = async (billingId: string) => {
    setDownloadingId(billingId)
    try {
      await downloadBoletoPDF(
        billingId,
        user?.token || '',
        user?.selectedContextEncoded
      )
    } catch (err: any) {
      addToast(err.response?.data?.message || 'Erro ao baixar boleto', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setDownloadingId(null)
    }
  }

  useEffect(() => {
    if (show) {
      fetchBillings()
      setFilterParcela('')
      setFilterStatus('all')
      setRowOverrides({})
    } else {
      setSelectedIds(new Set())
      setError(null)
      setRowOverrides({})
    }
  }, [show, contractId])

  const fetchBillings = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getContractBillingsForItau(
        contractId,
        user?.token || '',
        user?.selectedContextEncoded
      )
      setBillings(data)
    } catch (err) {
      setError('Erro ao carregar recebíveis')
    } finally {
      setLoading(false)
    }
  }

  // Group by payment part, filter, sort
  const groups = useMemo(() => {
    const grouped = new Map<string, BillingForItau[]>()
    for (const b of billings) {
      const key = b.payment_part_name || 'Sem etapa'
      if (!grouped.has(key)) grouped.set(key, [])

      // Filter by parcela number
      if (filterParcela.trim()) {
        if (!b.installment_number?.toString().includes(filterParcela.trim()))
          continue
      }

      // Filter by status
      if (
        filterStatus === 'pending' &&
        (b.status === 'paid' || b.has_itau_boleto)
      )
        continue
      if (filterStatus === 'emitido' && !b.has_itau_boleto) continue
      if (filterStatus === 'pago' && b.status !== 'paid') continue

      grouped.get(key)!.push(b)
    }

    const result: PaymentPartGroup[] = []
    for (const [name, items] of grouped) {
      if (items.length === 0) continue
      items.sort(
        (a, b) => (a.installment_number || 0) - (b.installment_number || 0)
      )
      result.push({ name, billings: items })
    }

    return result
  }, [billings, filterParcela, filterStatus])

  const selectableBillings = billings.filter((b) => b.can_generate_boleto)

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  const toggleGroup = (group: PaymentPartGroup) => {
    const next = new Set(selectedIds)
    const selectable = group.billings.filter((b) => b.can_generate_boleto)
    const allSelected = selectable.every((b) => next.has(b.id))
    if (allSelected) {
      selectable.forEach((b) => next.delete(b.id))
    } else {
      selectable.forEach((b) => next.add(b.id))
    }
    setSelectedIds(next)
  }

  const selectAll = () => {
    setSelectedIds(new Set(selectableBillings.map((b) => b.id)))
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const handleGenerate = async () => {
    if (selectedIds.size === 0) return
    setSubmitting(true)
    setError(null)

    try {
      // Build per-billing dates + waive-penalties maps
      const perBillingDates: { [id: string]: string } = {}
      const perBillingWaive: { [id: string]: boolean } = {}
      selectedIds.forEach((id) => {
        if (rowOverrides[id]?.date) perBillingDates[id] = rowOverrides[id].date
        if (rowOverrides[id]?.waivePenalties) perBillingWaive[id] = true
      })
      const result = await generateItauBoletos(
        Array.from(selectedIds),
        user?.token || '',
        user?.selectedContextEncoded,
        undefined,
        Object.keys(perBillingDates).length > 0 ? perBillingDates : undefined,
        Object.keys(perBillingWaive).length > 0 ? perBillingWaive : undefined
      )

      if (result.success.length > 0) {
        addToast(
          `${result.success.length} bolecode(s) gerado(s) com sucesso!`,
          { appearance: 'success', autoDismiss: true }
        )
      }

      if (result.errors.length > 0) {
        const errorMsgs = result.errors.map((e) => e.error).join('; ')
        addToast(`${result.errors.length} erro(s): ${errorMsgs}`, {
          appearance: 'error',
          autoDismiss: false
        })
      }

      setSelectedIds(new Set())
      await fetchBillings()
      onGenerated()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao gerar boletos')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Gerar Bolecodes Itaú</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Carregando recebíveis...</p>
          </div>
        ) : (
          <>
            <div
              className="d-flex justify-content-between align-items-center mb-3 flex-wrap"
              style={{ gap: 8 }}
            >
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <Form.Control
                  placeholder="Nº parcela..."
                  value={filterParcela}
                  onChange={(e) => setFilterParcela(e.target.value)}
                  size="sm"
                  style={{ width: 120 }}
                  type="number"
                />
                <Form.Control
                  as="select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  size="sm"
                  style={{ width: 150 }}
                >
                  <option value="all">Todos</option>
                  <option value="pending">Pendentes</option>
                  <option value="emitido">Emitidos</option>
                  <option value="pago">Pagos</option>
                </Form.Control>
              </div>
              <div>
                <small className="text-muted">
                  {selectedIds.size} de {selectableBillings.length} selecionados
                </small>
                {selectableBillings.length > 0 && (
                  <>
                    {' '}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ml-2"
                      onClick={
                        selectedIds.size === selectableBillings.length
                          ? deselectAll
                          : selectAll
                      }
                    >
                      {selectedIds.size === selectableBillings.length
                        ? 'Desmarcar todos'
                        : 'Selecionar todos'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {groups.length === 0 && (
              <p className="text-center text-muted">
                {filterParcela || filterStatus !== 'all'
                  ? 'Nenhum recebível encontrado para este filtro'
                  : 'Nenhum recebível encontrado'}
              </p>
            )}

            {groups.map((group) => {
              const selectableInGroup = group.billings.filter(
                (b) => b.can_generate_boleto
              )
              const allGroupSelected =
                selectableInGroup.length > 0 &&
                selectableInGroup.every((b) => selectedIds.has(b.id))

              return (
                <div key={group.name} className="mb-4">
                  <div
                    className="d-flex align-items-center mb-1"
                    style={{ gap: 8 }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={allGroupSelected}
                      onChange={() => toggleGroup(group)}
                      disabled={selectableInGroup.length === 0}
                    />
                    <strong>{group.name}</strong>
                    <small className="text-muted">
                      ({group.billings.length} parcela
                      {group.billings.length !== 1 ? 's' : ''})
                    </small>
                  </div>

                  <style>{`
                    .bolecode-table td, .bolecode-table th {
                      padding: 4px 6px !important;
                    }
                  `}</style>
                  <Table
                    striped
                    bordered
                    hover
                    size="sm"
                    className="mb-0 bolecode-table"
                    style={{ fontSize: 13 }}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: 30 }}></th>
                        <th>Parcela</th>
                        <th>Vencimento</th>
                        <th style={{ textAlign: 'right' }}>Original</th>
                        <th style={{ textAlign: 'right' }}>Corrigido</th>
                        <th style={{ width: 110, textAlign: 'center' }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.billings.map((b) => {
                        const statusBadge = (() => {
                          const isOverdue = moment(
                            b.itau_due_date || b.due_date
                          ).isBefore(moment(), 'day')
                          if (b.status === 'cancelled')
                            return (
                              <Badge variant="dark" className="ml-2">
                                CANCELADO
                              </Badge>
                            )
                          if (b.status === 'paid')
                            return (
                              <Badge variant="success" className="ml-2">
                                PAGO
                              </Badge>
                            )
                          if (b.has_itau_boleto && isOverdue)
                            return (
                              <Badge variant="danger" className="ml-2">
                                EMITIDO • ATRASADO
                              </Badge>
                            )
                          if (b.has_itau_boleto)
                            return (
                              <Badge variant="info" className="ml-2">
                                EMITIDO
                              </Badge>
                            )
                          if (isOverdue)
                            return (
                              <Badge variant="warning" className="ml-2">
                                ATRASADO
                              </Badge>
                            )
                          return null
                        })()
                        return (
                          <tr
                            key={b.id}
                            style={
                              b.status === 'cancelled'
                                ? {
                                    backgroundColor: '#f5f5f5',
                                    color: '#888',
                                    textDecoration: 'line-through'
                                  }
                                : b.status === 'paid'
                                ? { backgroundColor: '#f0faf0' }
                                : undefined
                            }
                          >
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedIds.has(b.id)}
                                onChange={() => toggleSelect(b.id)}
                                disabled={!b.can_generate_boleto}
                              />
                            </td>
                            <td>
                              {b.installment_number}
                              {statusBadge}
                            </td>
                            <td>
                              {b.can_generate_boleto ? (
                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: 4 }}
                                >
                                  <Form.Control
                                    type="date"
                                    size="sm"
                                    value={
                                      rowOverrides[b.id]?.date ||
                                      moment(
                                        b.itau_due_date || b.due_date
                                      ).format('YYYY-MM-DD')
                                    }
                                    onChange={(e) =>
                                      setRowDate(b.id, e.target.value)
                                    }
                                    onBlur={(e: any) => {
                                      const newDate = e.target.value
                                      const originalDate = moment(
                                        b.itau_due_date || b.due_date
                                      ).format('YYYY-MM-DD')
                                      if (newDate && newDate !== originalDate) {
                                        fetchPreview(b.id, newDate)
                                      }
                                    }}
                                    style={{
                                      width: 130,
                                      fontSize: 11,
                                      padding: '1px 4px',
                                      height: 24
                                    }}
                                  />
                                  {rowOverrides[b.id]?.previewing && (
                                    <Spinner animation="border" size="sm" />
                                  )}
                                  {rowOverrides[b.id]?.date &&
                                    !rowOverrides[b.id]?.previewing && (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        style={{
                                          padding: 0,
                                          fontSize: 10
                                        }}
                                        onClick={() => clearRowOverride(b.id)}
                                      >
                                        ✕
                                      </Button>
                                    )}
                                  <Form.Check
                                    type="checkbox"
                                    id={`waive-${b.id}`}
                                    label="s/ multa+juros"
                                    checked={
                                      !!rowOverrides[b.id]?.waivePenalties
                                    }
                                    onChange={() => {
                                      toggleWaivePenalties(b.id)
                                      // Fetch preview so we can show breakdown
                                      const currentDate =
                                        rowOverrides[b.id]?.date ||
                                        moment(
                                          b.itau_due_date || b.due_date
                                        ).format('YYYY-MM-DD')
                                      if (!rowOverrides[b.id]?.previewValue) {
                                        fetchPreview(b.id, currentDate)
                                      }
                                    }}
                                    style={{ fontSize: 11, marginLeft: 4 }}
                                    title="Emite bolecode sem multa/juros por atraso. Requer alçada de desconto pra registrar limpo no UAU."
                                  />
                                </div>
                              ) : (
                                <>
                                  {moment(b.itau_due_date || b.due_date).format(
                                    'DD/MM/YYYY'
                                  )}
                                  {b.itau_reissue_count > 0 && (
                                    <Badge
                                      variant="warning"
                                      className="ml-1"
                                      style={{ fontSize: 9 }}
                                      title={`${
                                        b.itau_reissue_count + 1
                                      }ª via. Original: ${moment(
                                        b.original_due_date
                                      ).format('DD/MM/YYYY')}`}
                                    >
                                      {b.itau_reissue_count + 1}ª VIA
                                    </Badge>
                                  )}
                                  {b.itau_penalties_waived && (
                                    <Badge
                                      variant="secondary"
                                      className="ml-1"
                                      style={{ fontSize: 9 }}
                                      title="Bolecode emitido sem multa/juros"
                                    >
                                      s/ M+J
                                    </Badge>
                                  )}
                                </>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {convertNumberToCurrencyFormat(Number(b.value))}
                            </td>
                            <td
                              style={{
                                textAlign: 'right',
                                fontWeight:
                                  b.paid_amount || b.calculated_total
                                    ? 600
                                    : 400
                              }}
                            >
                              {rowOverrides[b.id]?.previewValue != null
                                ? (() => {
                                    const ro = rowOverrides[b.id]
                                    const waiving =
                                      !!ro.waivePenalties &&
                                      ro.previewSemPenalidades != null
                                    const displayed = waiving
                                      ? ro.previewSemPenalidades!
                                      : ro.previewValue!
                                    const penTotal =
                                      (ro.previewMulta || 0) +
                                      (ro.previewJuros || 0)
                                    return (
                                      <span
                                        title={
                                          `Data: ${moment(ro.date).format(
                                            'DD/MM/YYYY'
                                          )}\n` +
                                          `Multa: R$ ${(
                                            ro.previewMulta || 0
                                          ).toFixed(2)}\n` +
                                          `Juros: R$ ${(
                                            ro.previewJuros || 0
                                          ).toFixed(2)}\n` +
                                          (waiving
                                            ? `Isento (economia R$ ${penTotal.toFixed(
                                                2
                                              )})`
                                            : `Total c/ penalidades`)
                                        }
                                        style={{
                                          color: waiving ? '#c17400' : '#1e7e34'
                                        }}
                                      >
                                        {convertNumberToCurrencyFormat(
                                          displayed
                                        )}
                                        {waiving && penTotal > 0 && (
                                          <div
                                            style={{
                                              fontSize: 9,
                                              color: '#c17400'
                                            }}
                                          >
                                            −
                                            {convertNumberToCurrencyFormat(
                                              penTotal
                                            )}{' '}
                                            M+J
                                          </div>
                                        )}
                                      </span>
                                    )
                                  })()
                                : b.status === 'paid' && b.paid_amount
                                ? convertNumberToCurrencyFormat(
                                    Number(b.paid_amount)
                                  )
                                : b.calculated_total
                                ? convertNumberToCurrencyFormat(
                                    Number(b.calculated_total)
                                  )
                                : '—'}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {b.has_itau_boleto &&
                                b.status === 'paid' &&
                                (b.uau_payment_registered_at ? (
                                  <Badge
                                    variant="success"
                                    style={{ fontSize: 10 }}
                                    title={`Sincronizado em ${moment(
                                      b.uau_payment_registered_at
                                    ).format('DD/MM/YYYY HH:mm')}`}
                                  >
                                    UAU ✓
                                  </Badge>
                                ) : (
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    title="Registrar pagamento no UAU"
                                    onClick={() => handleSyncUau(b.id)}
                                    disabled={syncingId === b.id}
                                    style={{ padding: '2px 6px', fontSize: 11 }}
                                  >
                                    {syncingId === b.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      'Sync UAU'
                                    )}
                                  </Button>
                                ))}
                              {b.has_itau_boleto && b.status !== 'paid' && (
                                <div
                                  className="d-flex justify-content-center"
                                  style={{ gap: 4 }}
                                >
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    title="Copiar PIX"
                                    onClick={() =>
                                      copyToClipboard(
                                        b.itau_pix_emv || '',
                                        'PIX'
                                      )
                                    }
                                    disabled={!b.itau_pix_emv}
                                    style={{ padding: '1px 4px', fontSize: 10 }}
                                  >
                                    PIX
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    title="Copiar linha digitável"
                                    onClick={() =>
                                      copyToClipboard(
                                        b.itau_linha_digitavel || '',
                                        'Linha digitável'
                                      )
                                    }
                                    disabled={!b.itau_linha_digitavel}
                                    style={{ padding: '1px 4px', fontSize: 10 }}
                                  >
                                    Linha
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    title="Baixar PDF do boleto"
                                    onClick={() => handleDownloadPDF(b.id)}
                                    disabled={downloadingId === b.id}
                                    style={{ padding: '2px 6px', fontSize: 11 }}
                                  >
                                    {downloadingId === b.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      'PDF'
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    title="Reenviar e-mail"
                                    onClick={() => handleResendEmail(b.id)}
                                    disabled={resendingId === b.id}
                                    style={{ padding: '1px 4px', fontSize: 10 }}
                                  >
                                    {resendingId === b.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      '✉'
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    title="Cancelar bolecode Itaú (parcela renegociada)"
                                    onClick={() => handleCancelBolecode(b.id)}
                                    disabled={cancellingId === b.id}
                                    style={{ padding: '1px 4px', fontSize: 10 }}
                                  >
                                    {cancellingId === b.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      '✕'
                                    )}
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )
            })}
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ padding: '12px 20px' }}>
        <div>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            className="mr-2"
          >
            Fechar
          </Button>
          <Button
            variant="success"
            onClick={handleGenerate}
            disabled={selectedIds.size === 0 || submitting}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Gerando...
              </>
            ) : (
              `Gerar bolecodes (${selectedIds.size})`
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
